/* eslint complexity: ["error", {max: 12}] -- many variants/optional/default features for Basecard */
import { useState } from "react";
import type { PropsWithChildren } from "react";

import isNil from "lodash/isNil";
import type { LayoutChangeEvent } from "react-native";

import Box from "@/ui/layout/Box";
import { tokens } from "@/ui/theme/tamagui.config";
import { ColorToken } from "@/ui/theme/tokens";
import { isWeb } from "@/utils/platform.util";
import { getMaxBorderRadius } from "@/utils/view.util";

const StandardCardWidth = isWeb() ? 240 : 200;

const BaseCardToken = {
  fullBleedPadding: 0,
  primaryBorderRadius: 18,
  primaryLargerRadius: 40,
  primaryPadding: 18,
  resourceCardBorderRadius: 16,
  resourceCardLargerRadius: 40,
  resourceCardPadding: 0,
  secondaryBorderRadius: 12,
  secondaryPadding: 16,
};

type BaseCardType =
  | "avatarBlock"
  | "fullBleed"
  | "primary"
  | "resourceCard"
  | "secondary";

interface BaseCardStyles {
  borderBottomLeftRadius: number;
  borderBottomRightRadius: number;
  borderTopLeftRadius: number;
  borderTopRightRadius: number;
  padding: number;
}

// eslint-disable-next-line @typescript-eslint/naming-convention -- PascalCase BaseCardStyles is the type
const baseCardStyles: Record<BaseCardType, BaseCardStyles> = {
  avatarBlock: {
    borderBottomLeftRadius: BaseCardToken.secondaryBorderRadius,
    borderBottomRightRadius: BaseCardToken.secondaryBorderRadius,
    borderTopLeftRadius: BaseCardToken.secondaryBorderRadius,
    borderTopRightRadius: BaseCardToken.secondaryBorderRadius,
    padding: BaseCardToken.primaryPadding,
  },
  fullBleed: {
    borderBottomLeftRadius: BaseCardToken.primaryBorderRadius,
    borderBottomRightRadius: BaseCardToken.primaryLargerRadius,
    borderTopLeftRadius: BaseCardToken.primaryBorderRadius,
    borderTopRightRadius: BaseCardToken.primaryBorderRadius,
    padding: BaseCardToken.fullBleedPadding,
  },
  primary: {
    borderBottomLeftRadius: BaseCardToken.primaryBorderRadius,
    borderBottomRightRadius: BaseCardToken.primaryLargerRadius,
    borderTopLeftRadius: BaseCardToken.primaryBorderRadius,
    borderTopRightRadius: BaseCardToken.primaryBorderRadius,
    padding: BaseCardToken.primaryPadding,
  },
  resourceCard: {
    borderBottomLeftRadius: BaseCardToken.resourceCardBorderRadius,
    borderBottomRightRadius: BaseCardToken.resourceCardLargerRadius,
    borderTopLeftRadius: BaseCardToken.resourceCardBorderRadius,
    borderTopRightRadius: BaseCardToken.resourceCardBorderRadius,
    padding: BaseCardToken.resourceCardPadding,
  },
  secondary: {
    borderBottomLeftRadius: BaseCardToken.secondaryBorderRadius,
    borderBottomRightRadius: BaseCardToken.secondaryBorderRadius,
    borderTopLeftRadius: BaseCardToken.secondaryBorderRadius,
    borderTopRightRadius: BaseCardToken.secondaryBorderRadius,
    padding: BaseCardToken.secondaryPadding,
  },
};

const getBaseCardStyles = (variant?: BaseCardType): BaseCardStyles => {
  if (isNil(variant)) {
    return baseCardStyles.primary;
  }

  return baseCardStyles[variant];
};

interface BaseCardProps extends BaseViewProps, PropsWithChildren {
  hasBorder?: boolean;
  isBorderDashed?: boolean;
  isDisabled?: boolean;
  isSelected?: boolean;
  maxHeight?: SizeDimension;
  maxWidth?: SizeDimension;
  onLongPress?: () => Promise<void> | void;
  onPress?: () => Promise<void> | void;
  // override this to resolve known issue with black solid line at bottom of dashed border
  // See: https://github.com/facebook/react-native/issues/37177
  shouldHideOverflow?: boolean;
  variant?: BaseCardType;
}

const { borderColor, borderWidth } = tokens;

const BaseCard = ({
  children,
  hasBorder = true,
  isBorderDashed,
  isDisabled,
  isSelected,
  maxHeight,
  maxWidth = "100%",
  onLongPress,
  onPress,
  shouldHideOverflow = true,
  testID,
  variant = "primary",
}: BaseCardProps) => {
  const {
    borderBottomLeftRadius,
    borderBottomRightRadius,
    borderTopLeftRadius,
    borderTopRightRadius,
    padding: baseCardPadding,
  } = getBaseCardStyles(variant);

  const [containerHeight, setContainerHeight] = useState<number | undefined>(
    undefined,
  );

  const handlePress = () => {
    if (isDisabled || isBorderDashed) {
      // TODO: PT-1436
      // setting isDisabled=true on the container box DOES prevent the onPress event from firing in the actual device
      // for some reason, in the jest test environment however, onPress is still being called
      return;
    }

    void onPress?.();
  };

  const handleLongPress = () => {
    if (isDisabled || isBorderDashed) {
      return;
    }

    void onLongPress?.();
  };

  const isPressDisabled = isDisabled || isBorderDashed || isNil(onPress);
  const maxBorderBottomRadius = getMaxBorderRadius({
    borderRadius: borderBottomRightRadius,
    containerHeight,
  });

  const handleLayoutChange = (event: LayoutChangeEvent) => {
    if (!isNil(containerHeight)) {
      return;
    }

    const { layout } = event.nativeEvent;
    const { height: expandedHeight } = layout;
    setContainerHeight(expandedHeight);
  };

  return (
    <Box
      aria-disabled={isPressDisabled}
      aria-selected={isSelected}
      backgroundColor={
        isSelected ? ColorToken.Primary10 : ColorToken.NeutralBackgroundPrimary
      }
      borderBottomLeftRadius={borderBottomLeftRadius}
      borderBottomRightRadius={maxBorderBottomRadius}
      borderColor={isSelected ? borderColor.selected : borderColor.primary}
      borderStyle={isBorderDashed ? "dashed" : "solid"}
      borderTopLeftRadius={borderTopLeftRadius}
      borderTopRightRadius={borderTopRightRadius}
      borderWidth={hasBorder ? borderWidth.primary : 0}
      disabled={isPressDisabled}
      height={maxHeight}
      onLayout={handleLayoutChange}
      onLongPress={handleLongPress}
      onPress={handlePress}
      overflow={shouldHideOverflow ? "hidden" : undefined}
      padding={baseCardPadding}
      pressStyle={{
        borderColor: borderColor.selected,
      }}
      testID={testID}
      width={maxWidth}
    >
      {children}
    </Box>
  );
};

export { baseCardStyles, BaseCardToken, getBaseCardStyles, StandardCardWidth };
export type { BaseCardProps };
export default BaseCard;
